import React, { useEffect, useMemo } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import PageContainer from "../layout/PageContainer.js";
import JsonLd from "../JsonLd.js";
import GuideCTA from "./GuideCTA.js";
import { buildGuideSectionJsonLd } from "../../utils/guideJsonLd.js";
import "../../mental-clarity-guide.css";

const DEFAULT_TITLE = "Ahavibes — Stop reacting. Start thinking clearly.";
const DEFAULT_DESCRIPTION =
  "A guided framework to examine interpretations, reduce mental noise, and make better decisions.";

const GuideSectionPage = ({ guide, sectionComponents, guideName, getVisual, getAccent, nextPillar }) => {
  const { sectionId } = useParams();
  const sections = guide.sections;
  const sectionIndex = sections.findIndex((s) => s.id === sectionId);
  const section = sections[sectionIndex];

  const SectionComponent = section ? sectionComponents[section.id] : null;
  const prevSection = sectionIndex > 0 ? sections[sectionIndex - 1] : null;
  const nextSection = sectionIndex < sections.length - 1 ? sections[sectionIndex + 1] : null;

  const heading = section ? `${section.position}. ${section.name}` : "";

  const jsonLd = useMemo(
    () => (section ? buildGuideSectionJsonLd(guide, section) : null),
    [section, guide]
  );

  useEffect(() => {
    if (!section) return;
    document.title = `${section.name} — ${guideName} | Ahavibes`;

    let meta = document.querySelector('meta[name="description"]');
    const createdMeta = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute("content");
    meta.setAttribute("content", section.description);

    window.scrollTo(0, 0);

    return () => {
      document.title = DEFAULT_TITLE;
      if (createdMeta) {
        meta.remove();
      } else {
        meta.setAttribute("content", previousDescription || DEFAULT_DESCRIPTION);
      }
    };
  }, [section, guideName]);

  if (!section || !SectionComponent) {
    return <Navigate to={guide.path} replace />;
  }

  return (
    <>
      {jsonLd ? <JsonLd data={jsonLd} id={`guide-section-${section.id}`} /> : null}
      <PageContainer size="wide" className="mental-clarity-guide">
        <nav className="guide-section-nav guide-section-nav--top" aria-label="Guide navigation">
          <Link to={guide.path} className="guide-section-nav__back">
            &larr; Back to guide
          </Link>
        </nav>

        <SectionComponent heading={heading} getVisual={getVisual} getAccent={getAccent} />

        <nav className="guide-section-nav" aria-label="Previous and next sections">
          <div className="guide-section-nav__links">
            {prevSection ? (
              <Link
                to={`${guide.path}/${prevSection.id}`}
                className="guide-section-nav__link guide-section-nav__link--prev"
              >
                &larr; {prevSection.position}. {prevSection.name}
              </Link>
            ) : (
              <span />
            )}
            {nextSection ? (
              <Link
                to={`${guide.path}/${nextSection.id}`}
                className="guide-section-nav__link guide-section-nav__link--next"
              >
                {nextSection.position}. {nextSection.name} &rarr;
              </Link>
            ) : null}
          </div>
        </nav>

        {sectionIndex === sections.length - 1 ? <GuideCTA nextPillar={nextPillar} /> : null}
      </PageContainer>
    </>
  );
};

export default GuideSectionPage;
